import { ApolloCache } from '@apollo/client'
import { isEmpty } from 'lodash'

import { getCurrentTime } from 'pages/Dashboard/utils/timeUtils'

import { DebitCardTransaction } from '../../CardSingle.types'

const PERSIST_SECONDS = 10

const checkIsOutstanding = ({
  settings,
  transaction,
}: {
  settings: {
    isReceiptOutstandingFilterSettingsOn: boolean
    isNoteOutstandingFilterSettingsOn: boolean
    isCategoryOutstandingFilterSettingsOn: boolean
  }
  transaction: DebitCardTransaction
}) => {
  return (
    (settings.isReceiptOutstandingFilterSettingsOn &&
      isEmpty(transaction.attachments)) ||
    (settings.isNoteOutstandingFilterSettingsOn && isEmpty(transaction.note)) ||
    (settings.isCategoryOutstandingFilterSettingsOn &&
      isEmpty(transaction.category))
  )
}

export function filterOutdatedTransactions<T extends { timestamp: number }>(
  transactions: T[]
): T[] {
  const currentTimestamp = getCurrentTime().valueOf()
  return transactions.filter((transaction) => {
    const timestampDiffInSecond = Math.floor(
      (currentTimestamp - transaction.timestamp) / 1000
    )
    return timestampDiffInSecond < PERSIST_SECONDS
  })
}

interface UpdateOutstandingTransactionsCountInDebitCardProps {
  prevTransaction: DebitCardTransaction
  nextTransaction: DebitCardTransaction
  settings: {
    isReceiptOutstandingFilterSettingsOn: boolean
    isNoteOutstandingFilterSettingsOn: boolean
    isCategoryOutstandingFilterSettingsOn: boolean
  }
  cache: ApolloCache<object>
}

export const updateOutstandingTransactionsCountInDebitCard = ({
  prevTransaction,
  nextTransaction,
  settings,
  cache,
}: UpdateOutstandingTransactionsCountInDebitCardProps) => {
  const isPrevTransactionOutstanding = checkIsOutstanding({
    settings,
    transaction: prevTransaction,
  })

  const isNextTransactionOutstanding = checkIsOutstanding({
    settings,
    transaction: nextTransaction,
  })

  if (isPrevTransactionOutstanding === isNextTransactionOutstanding) {
    return
  }

  cache.modify({
    id: cache.identify({
      id: nextTransaction.debitCardId,
      __typename: 'DebitCardV2',
    }),
    fields: {
      outstandingTransactions(oldOutstandingTransactions: number | null) {
        return (
          (oldOutstandingTransactions ?? 0) +
          (isNextTransactionOutstanding ? 1 : -1)
        )
      },
    },
  })
}
